import { ErrorBoundary } from '@sentry/react';
import { ExperimentalButtonIconWithTooltip } from '@statsbomb/kitbag-components';
import { VideoStatus } from '@statsbomb/parachute-types';
import { useAtomValue } from 'jotai';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { trackObjectOpenCloseEvent } from '@/analytics/snowplow/analytics';
import { userVideoAccessStatusAtom } from '@/atoms/user';
import { usePlayButtonFeedback } from '@/hooks/usePlayButtonFeedback';

const NavigateToVideoButtonBase = ({
  linkParams,
  userVideoAccessStatus,
  isLoading,
  isError,
  metricKey,
}: {
  linkParams?: string;
  userVideoAccessStatus?: VideoStatus;
  isLoading?: boolean;
  isError?: boolean;
  metricKey?: string;
}) => {
  const { t } = useTranslation('video');
  const navigate = useNavigate();
  const disabledReason = usePlayButtonFeedback(userVideoAccessStatus);
  const disabled = userVideoAccessStatus !== 'enabled' || isLoading || isError;

  return (
    <span className="invisible">
      <ExperimentalButtonIconWithTooltip
        disabledReason={disabledReason}
        disabled={disabled}
        isPending={isLoading}
        icon={userVideoAccessStatus === 'disabled' ? 'Lock' : 'Play'}
        size="small"
        title={t('playVideo')}
        variant="ghost"
        displayText="hidden"
        onClick={async () => {
          await trackObjectOpenCloseEvent({
            objectType: 'Video Synch',
            openVsClose: 'Open',
            conditionType: metricKey,
          });
          navigate(`./video${linkParams}`);
        }}
      >
        {t('playVideo')}
      </ExperimentalButtonIconWithTooltip>
    </span>
  );
};

const NavigateToVideoButtonWithData = ({ linkParams, metricKey }: { linkParams: string; metricKey?: string }) => {
  const userVideoAccessStatus = useAtomValue(userVideoAccessStatusAtom);

  return (
    <NavigateToVideoButtonBase
      linkParams={linkParams}
      metricKey={metricKey}
      userVideoAccessStatus={userVideoAccessStatus}
    />
  );
};

export const NavigateToVideoButton = ({ linkParams, metricKey }: { linkParams: string; metricKey?: string }) => (
  <ErrorBoundary fallback={<NavigateToVideoButtonBase isError />}>
    <Suspense fallback={<NavigateToVideoButtonBase isLoading />}>
      <NavigateToVideoButtonWithData linkParams={linkParams} metricKey={metricKey} />
    </Suspense>
  </ErrorBoundary>
);
